import { isNotDefined } from '@sgme/fp';
import type {
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  FilterChangedEvent,
  SortChangedEvent,
  ToolPanelVisibleChangedEvent,
} from 'ag-grid-community';
import { useMemo } from 'react';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';

const eventValueMaxArrayLenth = 5;

export function useGridEventsTrackers(grid: 'cash' | 'order' | 'option' | 'accumulator') {
  const track = useTrackEvent();

  return useMemo(
    () => ({
      handleSortChanged: ({ source, columns: [column] = [] }: SortChangedEvent) => {
        if (
          source !== 'uiColumnSorted' || // we don't want to react to initial sorting for instance
          isNotDefined(column)
        ) {
          return;
        }
        track(`Change ${grid} grid sort`, `Column "${column.getColDef().headerName ?? '?'}"`);
      },

      // filter value is modified and is applied
      handleFilterChanged: ({ source, afterDataChange = false, columns: [column] = [] }: FilterChangedEvent) => {
        if (
          source !== 'columnFilter' || // we want to react to user interaction
          afterDataChange ||
          isNotDefined(column)
        ) {
          return;
        }
        track(`Apply ${grid} grid filter`, `Column "${column.getColDef().headerName ?? '?'}"`);
      },

      handleToolPanelVisibleChange: ({ source, visible }: ToolPanelVisibleChangedEvent) => {
        if (source !== 'sideBarButtonClicked') {
          return;
        }
        track(visible ? `Open ${grid} grid sidebar` : `Close ${grid} grid sidebar`);
      },

      handleColumnVisible: ({ source, visible, columns }: ColumnVisibleEvent) => {
        if (source !== 'toolPanelUi' || isNotDefined(columns) || isNotDefined(columns[0])) {
          return;
        }
        const eventValue =
          columns
            .slice(0, eventValueMaxArrayLenth)
            .map((col) => col.getColDef().headerName ?? '?')
            .join(', ') +
          (columns.length > eventValueMaxArrayLenth ? ` and ${columns.length - eventValueMaxArrayLenth} more` : '');
        track(visible ? `Show ${grid} grid column` : `Hide ${grid} grid column`, eventValue);
      },

      handleColumnMoved: ({ source, finished, column }: ColumnMovedEvent) => {
        if ((source !== 'uiColumnMoved' && source !== 'toolPanelUi') || !finished || isNotDefined(column)) {
          return;
        }

        track(
          `Move ${grid} grid column via ${source === 'uiColumnMoved' ? 'column header' : 'tool panel'}`,
          column.getColDef().headerName ?? '?',
        );
      },

      handleColumnResized: ({ source, finished, column }: ColumnResizedEvent) => {
        if (source !== 'uiColumnResized' || !finished || isNotDefined(column)) {
          return;
        }
        track(`Resize ${grid} grid column`, column.getColDef().headerName ?? '?');
      },
    }),
    [grid],
  );
}
